import { View } from 'react-native';
import { useEffect } from 'react';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withRepeat,
  withSequence,
  withTiming,
} from 'react-native-reanimated';
import { cn } from '@/lib/utils';

/** Stagger between the three dots — gives the classic typing "wave". */
const DOT_DELAYS = [0, 150, 300];
const PULSE_DURATION = 450;

const Dot = ({ delay }: { delay: number }) => {
  const opacity = useSharedValue(0.3);

  useEffect(() => {
    opacity.value = withDelay(
      delay,
      withRepeat(
        withSequence(
          withTiming(1, { duration: PULSE_DURATION }),
          withTiming(0.3, { duration: PULSE_DURATION }),
        ),
        -1,
      ),
    );
  }, [delay, opacity]);

  const style = useAnimatedStyle(() => ({ opacity: opacity.value }));

  return (
    <Animated.View style={style}>
      <View className="w-2 h-2 rounded-full bg-text-muted" />
    </Animated.View>
  );
};

export interface TypingIndicatorProps {
  /** Smaller right-rail variant — matches the compact assistant bubble. */
  compact?: boolean;
}

/**
 * Assistant "thinking" bubble: three pulsating dots. Visually matches the
 * assistant text bubble in {@link ChatMessage} (left-aligned, rounded surface)
 * so it reads as a placeholder for the incoming reply.
 */
export const TypingIndicator = ({ compact }: TypingIndicatorProps) => {
  return (
    <View
      className={cn(
        'rounded-2xl p-3.5 self-start',
        compact ? 'bg-background' : 'bg-surface',
      )}
    >
      <View className="flex-row items-center gap-x-1">
        {DOT_DELAYS.map((delay) => (
          <Dot key={delay} delay={delay} />
        ))}
      </View>
    </View>
  );
};
